import api from "services/commercialDashboard";
import candidateApi from "services/candidate";

const state = {
  selectedCommercialId: null,
  summary: null,
  detail: null,
  drilldown: [],
  drilldownPagination: null,
  drilldownMeta: null,
  drilldownSelectedId: null,
  drilldownItem: null,
  focusSettings: null,
  loading: false,
  detailLoading: false,
  drilldownLoading: false,
  drilldownItemLoading: false,
  focusLoading: false,
  error: null
};

const getters = {
  selectedCommercialId: state => state.selectedCommercialId,
  commercialDashboardSummary: state => state.summary,
  commercialDashboardDetail: state => state.detail,
  commercialDashboardDrilldown: state => state.drilldown,
  commercialDashboardDrilldownPagination: state => state.drilldownPagination,
  commercialDashboardDrilldownMeta: state => state.drilldownMeta,
  commercialDashboardDrilldownSelectedId: state => state.drilldownSelectedId,
  commercialDashboardDrilldownItem: state => state.drilldownItem,
  commercialDashboardFocusSettings: state => state.focusSettings,
  commercialDashboardLoading: state => state.loading,
  commercialDashboardDetailLoading: state => state.detailLoading,
  commercialDashboardDrilldownLoading: state => state.drilldownLoading,
  commercialDashboardDrilldownItemLoading: state => state.drilldownItemLoading,
  commercialDashboardFocusLoading: state => state.focusLoading,
  commercialDashboardError: state => state.error
};

const mutations = {
  setSelectedCommercialId(state, payload) {
    state.selectedCommercialId = payload;
  },
  setLoading(state, payload) {
    state.loading = payload;
  },
  setDetailLoading(state, payload) {
    state.detailLoading = payload;
  },
  setDrilldownLoading(state, payload) {
    state.drilldownLoading = payload;
  },
  setDrilldownItemLoading(state, payload) {
    state.drilldownItemLoading = payload;
  },
  setFocusLoading(state, payload) {
    state.focusLoading = payload;
  },
  setError(state, payload) {
    state.error = payload;
  },
  setSummary(state, payload) {
    state.summary = payload;
  },
  setDetail(state, payload) {
    state.detail = payload;
  },
  setDrilldown(state, payload) {
    state.drilldown = payload.applications || [];
    state.drilldownPagination = payload.meta ? payload.meta.pagination : null;
    state.drilldownMeta = payload.meta || null;
  },
  setDrilldownSelectedId(state, payload) {
    state.drilldownSelectedId = payload;
  },
  setDrilldownItem(state, payload) {
    state.drilldownItem = payload;
  },
  setFocusSettings(state, payload) {
    state.focusSettings = payload;
  }
};

const actions = {
  setSelectedCommercialId({ commit }, commercialId) {
    commit("setSelectedCommercialId", commercialId);
  },
  selectDrilldownItem({ commit }, applicationId) {
    commit("setDrilldownSelectedId", applicationId);
  },
  clearDrilldownItem({ commit }) {
    commit("setDrilldownSelectedId", null);
    commit("setDrilldownItem", null);
  },
  async fetchDashboard({ commit, state }, query = {}) {
    commit("setLoading", true);
    commit("setError", null);

    const params = Object.assign({}, query);
    if (!Object.prototype.hasOwnProperty.call(params, "commercial_id") && state.selectedCommercialId) {
      params.commercial_id = state.selectedCommercialId;
    }

    const response = await api.dashboard(params);

    if (response && response.status === 200) {
      commit("setSummary", response.data.data);
      commit(
        "setSelectedCommercialId",
        response.data.data.selected_commercial ? response.data.data.selected_commercial.id : null
      );
    } else {
      commit("setError", response);
    }

    commit("setLoading", false);
    return response;
  },
  async fetchMyRelancesDetail({ commit, state }, query = {}) {
    commit("setDetailLoading", true);
    const params = Object.assign({}, query);

    if (!Object.prototype.hasOwnProperty.call(params, "commercial_id") && state.selectedCommercialId) {
      params.commercial_id = state.selectedCommercialId;
    }

    const response = await api.myRelancesDetail(params);

    if (response && response.status === 200) {
      commit("setDetail", response.data.data);
    } else {
      commit("setError", response);
    }

    commit("setDetailLoading", false);
    return response;
  },
  async fetchDrilldown({ commit, state }, query = {}) {
    commit("setDrilldownLoading", true);
    const params = Object.assign({}, query);

    if (!Object.prototype.hasOwnProperty.call(params, "commercial_id") && state.selectedCommercialId) {
      params.commercial_id = state.selectedCommercialId;
    }

    const response = await api.drilldowns(params);

    if (response && response.status === 200) {
      commit("setDrilldown", response.data);
    } else {
      commit("setError", response);
    }

    commit("setDrilldownLoading", false);
    return response;
  },
  async fetchDrilldownItem({ commit, state }, payload) {
    commit("setDrilldownItemLoading", true);
    const params = Object.assign({}, payload && payload.query ? payload.query : {});

    if (!Object.prototype.hasOwnProperty.call(params, "commercial_id") && state.selectedCommercialId) {
      params.commercial_id = state.selectedCommercialId;
    }

    const response = await api.drilldownItem(payload.applicationId, params);

    if (response && response.status === 200) {
      commit("setDrilldownItem", response.data.data);
    } else {
      commit("setDrilldownItem", null);
      commit("setError", response);
    }

    commit("setDrilldownItemLoading", false);
    return response;
  },
  async updateDrilldownReminder({ commit }, payload) {
    const response = await candidateApi.saveApplicationReminder({
      id: payload.applicationId,
      reminder_employee: payload.reminderEmployeeId
    });

    if (!(response && response.status === 200)) {
      commit("setError", response);
    }

    return response;
  },
  async fetchFocusSettings({ commit }) {
    commit("setFocusLoading", true);
    const response = await api.focusSettings();

    if (response && response.status === 200) {
      commit("setFocusSettings", response.data.data);
    } else {
      commit("setError", response);
    }

    commit("setFocusLoading", false);
    return response;
  },
  async saveFocusSettings({ commit }, payload) {
    commit("setFocusLoading", true);
    const response = await api.updateFocusSettings(payload);

    if (response && response.status === 200) {
      commit("setFocusSettings", response.data.data);
    } else {
      commit("setError", response);
    }

    commit("setFocusLoading", false);
    return response;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
